import React, { MutableRefObject, useMemo } from 'react'
import { MessageDescriptor } from 'react-intl'
import { ImageSourcePropType } from 'react-native'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import {
  AIDraftStatus,
  AppointmentSummary,
  EntityTypes,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { NoNewActivities } from '@lyrahealth-inc/ui-core-crossplatform/src/assets'
import { AIGeneratedTag } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/aiGeneratedTag/AIGeneratedTag'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InsertIntoTextIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InsertIntoTextIcon'
import { LoadingSpinner } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingSpinner'
import { Image } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/image/Image'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { useGetFeedbackQuery } from './data/notesApi'
import { actions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { isFullyInViewport, scrollUntilVisible } from '../../common/utils/utils'
import { getAuthUserId } from '../../data/auth/authSelectors'
import PanelFeedbackSection from '../clients/PanelFeedbackSection'
type ClientNotesPanelProps = {
  appointmentId: string
  feedbackFormRef: MutableRefObject<any>
  fieldName: string
  noteAppointmentSummary: AppointmentSummary
  noteId?: string
  noteRef: MutableRefObject<any>
  noteType: string
  panelTitle: MessageDescriptor | string
  promptType: string
  customContainerStyle?: CSSObject
}

const HeaderContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  padding: `0 ${spacing['16px']} 10px ${spacing['16px']}`,
  border: 0,
  justifyContent: 'space-between',
  minHeight: '59px',
}))

const SubsectionContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing, colors } }) => ({
  margin: `${spacing['16px']} ${spacing['16px']} 0`,
  border: `1px solid ${colors.borderDefault}`,
  borderRadius: '8px',
}))

const ImageContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: 'auto',
  marginRight: 'auto',
  padding: spacing['24px'],
}))

const ImageHeader = styled(Title)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  textAlign: 'center',
  paddingTop: spacing['8px'],
}))
const AITagContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  alignSelf: 'flex-start',
  marginBottom: spacing['12px'],
}))

const ImageText = styled(BodyText)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  textAlign: 'center',
  paddingTop: spacing['8px'],
  maxWidth: '280px',
  marginLeft: 'auto',
  marginRight: 'auto',
}))

const ProcessingImage = styled(Image)({
  width: '226px',
  height: '174px',
})

const SummaryContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  padding: spacing['16px'],
  borderRadius: spacing['16px'],
}))

const InsertButtonContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  paddingTop: spacing['12px'],
  width: '100%',
}))

const Container = styled.View<{ customContainerStyle?: CSSObject }>(({ customContainerStyle }) => ({
  overflow: 'auto',
  height: '100%',
  ...customContainerStyle,
}))

const ClientNotesPanel: React.FC<ClientNotesPanelProps> = ({
  appointmentId,
  fieldName,
  feedbackFormRef,
  noteAppointmentSummary,
  noteId,
  noteRef,
  noteType,
  panelTitle,
  promptType,
  customContainerStyle,
}) => {
  const { colors } = useTheme()
  const userId = useSelector(getAuthUserId)
  const { data: feedback, isLoading: isLoadingFeedback } = useGetFeedbackQuery(
    noteId
      ? {
          providerId: userId,
          entityId: noteId,
        }
      : skipToken,
  )
  const status: AIDraftStatus = noteAppointmentSummary.status

  const pasteIntoField = async (
    noteRef: MutableRefObject<any>,
    fieldName: string,
    summary: string,
    noteType: string,
    appointmentId: string,
  ): Promise<void> => {
    const el = document?.getElementById(fieldName) as HTMLElement | null

    // Only wait if a scroll is needed; paste immediately if already in viewport or element missing
    if (el && !isFullyInViewport(el)) {
      await scrollUntilVisible(el)
    }
    const initialFieldValue = noteRef.current.getFieldState(fieldName).value
    noteRef.current.mutators.setFormAttribute(
      fieldName,
      initialFieldValue ? `${initialFieldValue}\n\n${summary}` : summary,
    )
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: actions.AI_DRAFTS_PASTE_INTO_NOTE,
      details: { noteType, appointmentId },
    })
  }

  const summary: string | null = useMemo(() => {
    if (status === AIDraftStatus.COMPLETE && promptType) {
      const fieldSummary = noteAppointmentSummary?.summary?.find(({ prompt_type }: { prompt_type: string }) => {
        return promptType === prompt_type
      })
      if (fieldSummary) {
        return fieldSummary.summary_text
      }
    }
    return null
  }, [noteAppointmentSummary, promptType, status])

  const renderDraft = () => {
    if ([AIDraftStatus.IN_PROGRESS, AIDraftStatus.NOT_STARTED].includes(status)) {
      return (
        <SubsectionContainer>
          <ImageContainer>
            <ProcessingImage source={NoNewActivities as ImageSourcePropType} />
          </ImageContainer>
          <ImageHeader size={TitleSize.SMALL} text='Draft not ready yet' color={colors.textPrimary} />
          <ImageText
            testID={tID('ClientNotesPanel-draftNotReadyText')}
            text='We’re still processing your draft. When it’s ready you can find it here'
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
          />
        </SubsectionContainer>
      )
    } else if (summary) {
      return (
        <SubsectionContainer>
          <SummaryContainer>
            <BodyText
              testID={tID(`ClientNotesPanel-summaryText`)}
              text={summary}
              size={BodyTextSize.DEFAULT}
              color={colors.textSecondary}
            />
            <InsertButtonContainer>
              <SecondaryButton
                leftIcon={<InsertIntoTextIcon color={colors.iconActive} />}
                text='Insert into note'
                onPress={async () => {
                  await pasteIntoField(noteRef, fieldName, summary as string, noteType, appointmentId)
                }}
                fullWidth
                testID={tID('ClientNotesPanel-pasteIntoNoteButton')}
              />
            </InsertButtonContainer>
          </SummaryContainer>
        </SubsectionContainer>
      )
    } else {
      return null
    }
  }

  return (
    <Container customContainerStyle={customContainerStyle}>
      <HeaderContainer>
        <AITagContainer>
          <AIGeneratedTag />
        </AITagContainer>
        <Title size={TitleSize.SMALL} text={`Draft: ${panelTitle}`} color={colors.textPrimary} />
      </HeaderContainer>
      {isLoadingFeedback ? (
        <SubsectionContainer>
          <ImageContainer>
            <LoadingSpinner size={60} />
          </ImageContainer>
          <ImageHeader size={TitleSize.SMALL} text='Fetching draft' color={colors.textPrimary} />
          <ImageText
            testID={tID(`ClientNotesPanel-loadingDraftText`)}
            text='This should only take a second'
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
          />
        </SubsectionContainer>
      ) : (
        <>
          {renderDraft()}
          {status === AIDraftStatus.COMPLETE && noteId && (
            <PanelFeedbackSection
              appointmentId={appointmentId}
              feedbackFormRef={feedbackFormRef}
              entityId={noteId}
              entityType={EntityTypes.NOTES}
              noteType={noteType}
              promptType={promptType}
              feedback={feedback}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default ClientNotesPanel
